import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "../lib/api";
import MainHeader from "../components/MainHeader";
import MainFooter from "../components/MainFooter";
import { ArrowUpRight, MapPin } from "lucide-react";

export default function FilmmakerPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { document.body.classList.remove("theme-mini"); }, []);
  useEffect(() => {
    api.get(`/filmmakers/${slug}`).then(({ data }) => setData(data)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return null;
  if (!data) return (
    <div className="min-h-screen bg-gs-cream"><MainHeader /><div className="max-w-3xl mx-auto px-6 py-20"><p className="font-serif text-3xl italic text-gs-ink/60">Filmmaker not found.</p></div></div>
  );

  const { filmmaker: fm, films } = data;
  const accent = fm.accent_color || "#C84B31";

  return (
    <div data-testid="filmmaker-page" className="min-h-screen bg-gs-cream">
      <MainHeader />
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-16 pb-12 grid md:grid-cols-12 gap-10 items-end">
        <div className="md:col-span-7">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em]" style={{ color: accent }}>Filmmaker</p>
          <h1 className="font-serif text-5xl md:text-7xl text-gs-ink mt-3 leading-[1.0]">{fm.name}</h1>
          {fm.location && (
            <p className="font-mono text-xs uppercase tracking-widest text-gs-dust mt-4 inline-flex items-center gap-2">
              <MapPin size={12} /> {fm.location}
            </p>
          )}
          <p className="font-serif text-xl text-gs-ink/80 mt-8 leading-relaxed max-w-2xl">{fm.bio}</p>
        </div>
        <div className="md:col-span-5">
          {fm.portrait_url && (
            <img src={fm.portrait_url} alt={fm.name} className="w-full aspect-[4/5] object-cover rounded-md" />
          )}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="flex items-end justify-between mb-10">
          <h2 className="font-serif text-4xl text-gs-ink">Filmography</h2>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gs-dust">{films.length} {films.length === 1 ? "film" : "films"}</p>
        </div>
        {films.length === 0 ? (
          <p className="font-serif italic text-2xl text-gs-ink/60">No films released yet.</p>
        ) : (
          <div className="grid md:grid-cols-3 gap-6">
            {films.map((f, i) => (
              <Link
                key={f.id}
                to={`/film/${f.slug}`}
                data-testid={`filmmaker-film-${i}`}
                className="group block"
              >
                <div className="aspect-[3/4] overflow-hidden rounded-md bg-gs-ink">
                  <img src={f.poster_url} alt={f.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="mt-4 flex items-start justify-between gap-3">
                  <div>
                    <p className="font-display text-2xl text-gs-ink tracking-wide">{f.title}</p>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-gs-dust mt-1">{f.year} · {f.genre} · {f.rating}</p>
                  </div>
                  <ArrowUpRight size={16} className="text-gs-ember mt-1" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
      <MainFooter />
    </div>
  );
}
